import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { BEACON_FONTS, BEACON_PRESETS, BANNER_SIZES, SECTION_SIZES, fontCss } from "../lib/beaconStyles";

const MATCH = "match";
const chip = (on) => `px-2.5 py-1 border text-xs font-body transition-colors ${on ? "border-[#14A89A] text-white bg-[#14A89A]/10" : "border-white/15 text-zinc-300 hover:bg-white/5"}`;
const trig = "h-8 bg-[#121212] border-white/15 text-white text-sm rounded-none focus:ring-0 focus:ring-offset-0";

export const FontSelect = ({ value, allowMatch, onPick, testid, matchLabel = "Same as title" }) => (
  <Select value={value || (allowMatch ? MATCH : "cabinet")} onValueChange={(v) => onPick(v === MATCH ? "" : v)}>
    <SelectTrigger className={`${trig} w-full`} data-testid={testid} style={{ fontFamily: value ? fontCss(value) : undefined }}><SelectValue /></SelectTrigger>
    <SelectContent className="bg-[#121212] border-white/15 text-white">
      {allowMatch && <SelectItem value={MATCH} data-testid={`${testid}-opt-match`}>{matchLabel}</SelectItem>}
      {BEACON_FONTS.map((f) => <SelectItem key={f.key} value={f.key} style={{ fontFamily: f.css }} data-testid={`${testid}-opt-${f.key}`}>{f.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const ColorSwatch = ({ value, fallback, accent, allowMatch, onPick, testid, matchLabel = "Same as title" }) => {
  const [open, setOpen] = useState(false);
  const shown = value || fallback || "#FFFFFF";
  const pick = (v) => { onPick(v); setOpen(false); };
  const opt = "w-full text-left px-2 py-1.5 font-body text-xs text-zinc-300 hover:bg-white/5 flex items-center gap-2";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} aria-label="Choose colour" className="w-8 h-8 rounded-full border-2 border-white/20 hover:border-white/50 transition-colors shrink-0" style={{ background: shown }} />
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1.5 bg-[#121212] border-white/15" data-testid={`${testid}-menu`}>
        {allowMatch && <button type="button" className={opt} onClick={() => pick("")} data-testid={`${testid}-match`}><span className="w-3 h-3 rounded-full border border-white/30" style={{ background: fallback || "#FFFFFF" }} />{matchLabel}</button>}
        <button type="button" className={opt} onClick={() => pick("#FFFFFF")} data-testid={`${testid}-white`}><span className="w-3 h-3 rounded-full bg-white" />White</button>
        <button type="button" className={opt} onClick={() => pick(accent)} data-testid={`${testid}-accent`}><span className="w-3 h-3 rounded-full" style={{ background: accent }} />Match accent</button>
        <label className={`${opt} cursor-pointer`}>
          <input type="color" value={shown} onChange={(e) => onPick(e.target.value)} data-testid={`${testid}-custom`} className="w-4 h-4 bg-transparent border-0 p-0 cursor-pointer" />Custom…
        </label>
      </PopoverContent>
    </Popover>
  );
};

const Row = ({ label, children, testid }) => (
  <div className="grid grid-cols-[88px_1fr_auto] sm:grid-cols-[120px_1fr_auto] items-center gap-2 sm:gap-3" data-testid={testid}>
    <span className="font-body text-xs text-zinc-400">{label}</span>
    {children}
  </div>
);

const Presets = ({ form, updateForm }) => (
  <div className="flex flex-wrap gap-2">
    {BEACON_PRESETS.map((p) => {
      const active = form.heading_font === p.heading_font && (form.accent_color || "").toLowerCase() === p.accent_color.toLowerCase() && (form.heading_color || "").toLowerCase() === p.heading_color.toLowerCase();
      return (
        <button key={p.key} type="button" data-testid={`beacon-preset-${p.key}`}
          onClick={() => updateForm({ heading_font: p.heading_font, accent_color: p.accent_color, heading_color: p.heading_color, tagline_font: "", tagline_color: "", section_font: "", section_color: "" })}
          className={`inline-flex items-center px-3 py-1.5 border font-body text-xs transition-colors ${active ? "border-white text-white bg-white/5" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}
          style={{ fontFamily: fontCss(p.heading_font) }}>
          <span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ background: p.accent_color }} />{p.label}
        </button>
      );
    })}
  </div>
);

const BannerLine = ({ form, updateForm }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="beacon-style-banner">
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="font-body text-xs text-zinc-400 mr-1">Banner</span>
      {BANNER_SIZES.map((s) => <button key={s.key} type="button" data-testid={`beacon-banner-size-${s.key}`} onClick={() => updateForm({ banner_size: s.key })} className={chip((form.banner_size || "standard") === s.key)}>{s.label}</button>)}
    </div>
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="font-body text-xs text-zinc-400 mr-1">Title</span>
      {[["below", "Below"], ["on", "On banner"]].map(([k, l]) => <button key={k} type="button" data-testid={`beacon-title-pos-${k}`} onClick={() => updateForm({ title_position: k })} className={chip((form.title_position || "below") === k)}>{l}</button>)}
    </div>
  </div>
);

export const BeaconStylePanel = ({ form, updateForm }) => {
  const accent = form.accent_color || "#14A89A";
  const [more, setMore] = useState(!!(form.tagline_font || form.tagline_color || form.section_font || form.section_color || (form.section_size && form.section_size !== "md")));
  return (
    <div className="space-y-4" data-testid="beacon-style-panel">
      <div>
        <div className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500 mb-1.5">Quick looks</div>
        <Presets form={form} updateForm={updateForm} />
      </div>
      <BannerLine form={form} updateForm={updateForm} />
      <p className="font-body text-[11px] text-zinc-600 -mt-2">Banner has its own words or logo? Keep the title below it. Changed the size? Re-upload your banner to reposition it.</p>
      <div className="border-t border-white/10 pt-4 space-y-3">
        <Row label="Main title" testid="beacon-style-title">
          <FontSelect value={form.heading_font} onPick={(v) => updateForm({ heading_font: v })} testid="beacon-font-select-title" />
          <ColorSwatch value={form.heading_color} accent={accent} onPick={(v) => updateForm({ heading_color: v })} testid="beacon-color-title" />
        </Row>
        <button type="button" onClick={() => setMore((m) => !m)} data-testid="beacon-style-more-toggle" className="flex items-center gap-1 font-body text-xs text-[#14A89A] hover:underline">
          {more ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {more ? "Hide extra options" : "Fine-tune tagline & section titles"}
        </button>
        {more && (
          <div className="space-y-3" data-testid="beacon-style-more">
            <Row label="Tagline" testid="beacon-style-tagline">
              <FontSelect value={form.tagline_font} allowMatch onPick={(v) => updateForm({ tagline_font: v })} testid="beacon-font-select-tagline" />
              <ColorSwatch value={form.tagline_color} fallback={form.heading_color} allowMatch accent={accent} onPick={(v) => updateForm({ tagline_color: v })} testid="beacon-color-tagline" />
            </Row>
            <Row label="Section titles" testid="beacon-style-sections">
              <div className="flex gap-2 min-w-0">
                <div className="flex-1 min-w-0"><FontSelect value={form.section_font} allowMatch onPick={(v) => updateForm({ section_font: v })} testid="beacon-font-select-section" /></div>
                <Select value={form.section_size || "md"} onValueChange={(v) => updateForm({ section_size: v })}>
                  <SelectTrigger className={`${trig} w-[96px] shrink-0`} data-testid="beacon-ssize-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/15 text-white">
                    {SECTION_SIZES.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`beacon-ssize-${s.key}`}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <ColorSwatch value={form.section_color} fallback={form.heading_color} allowMatch accent={accent} onPick={(v) => updateForm({ section_color: v })} testid="beacon-color-section" />
            </Row>
          </div>
        )}
      </div>
    </div>
  );
};
